import DocumentPage from './DocumentPage';

export const expensesDocument = {
  slug: 'expenses',
  route: '/expenses',
  title: 'Expenses',
  category: 'Finance & Procurement',
  format: 'DOCX',
  description:
    'The Expenses module helps employees record business expenses, move them through approval stages, and complete reimbursement with accounting support.',
  metrics: [
    { label: 'Primary focus', value: 'Expense control' },
    { label: 'Core records', value: 'Expenses and reports' },
    { label: 'Approval model', value: 'To report to done' },
  ],
  workflow: [
    'Employees create expense entries from My Expenses and group them into a report.',
    'Managers review the report through the approval workflow from To Submit to Approved.',
    'Accounting posts the approved report, registers payment, and completes reimbursement.',
  ],
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      body: [
        'The Expenses module in EduX ERP is used to manage and track expenses incurred by employees of the institution such as faculty members, administrative staff, and other employees. It helps record business-related expenses such as travel, food, office supplies, and other operational costs.',
        'Employees can create expense records and submit them to their managers for approval. Once approved, the expenses can be processed by the accounts department for reimbursement.',
      ],
    },
    {
      id: 'my-expenses',
      title: 'My Expenses',
      body: [
        'Go to Expenses -> My Expenses -> My Expenses.',
        'The My Expenses menu allows employees to create and manage their individual expenses.',
      ],
      figure: {
        src: '/assets/doc-images/expenses/image.png',
        alt: 'My Expenses record in To Report state',
        caption: 'Employees create an expense entry first, and it starts in the To Report state.',
      },
      subsections: [
        {
          id: 'expense-status-workflow',
          title: 'Expense Status Workflow',
          body: [
            'Each expense created in the system passes through different states during the approval process. These states help track the progress of the expense from creation to final reimbursement.',
          ],
        },
        {
          id: 'to-report',
          title: 'To Report',
          body: [
            'This is the initial state of an expense after it is created. In this stage, the expense is saved but not yet added to an expense report. The employee can review or modify the expense before including it in a report.',
            'Purpose: to indicate that the expense is recorded but still needs to be grouped into an expense report.',
          ],
          figure: {
            src: '/assets/doc-images/expenses/image.png',
            alt: 'Expense form in To Report state',
            caption: 'To Report means the expense entry exists but has not yet been grouped into a report.',
          },
        },
        {
          id: 'to-submit',
          title: 'To Submit',
          body: [
            'In this stage, the expense has been added to an expense report but has not yet been submitted to the manager for approval.',
            'Purpose: to show that the expense report is prepared and waiting for the employee to submit it for approval.',
          ],
          figure: {
            src: '/assets/doc-images/expenses/image2.png',
            alt: 'Expense report summary in To Submit state',
            caption: 'To Submit shows that the expense has been grouped into a report and is ready to be sent for approval.',
          },
        },
        {
          id: 'submitted',
          title: 'Submitted',
          body: [
            'In this stage, the expense report has been submitted for approval and is waiting for manager review.',
            'Purpose: to show that the expense report is prepared and has entered the approval queue.',
          ],
          figure: {
            src: '/assets/doc-images/expenses/image3.png',
            alt: 'Expense record in Submitted state',
            caption: 'Submitted shows that the expense has moved beyond draft preparation and into approval review.',
          },
        },
        {
          id: 'approved',
          title: 'Approved',
          body: [
            'After reviewing the expense details and attached receipts, the manager approves the expense report.',
            'Purpose: to confirm that the manager has verified and accepted the expense.',
          ],
          figure: {
            src: '/assets/doc-images/expenses/image4.png',
            alt: 'Expense report summary in Approved state',
            caption: 'Approved confirms that the manager has accepted the expense report.',
          },
        },
        {
          id: 'posted',
          title: 'Posted',
          body: [
            'Once the expense report is approved, it is posted to the accounting system. At this stage, the expense entry is recorded in accounting, and reimbursement processing can begin.',
            'Purpose: to record the approved expense in the accounting records of the institution.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/expenses/image5.png',
              alt: 'Expense report summary in Posted state',
              caption: 'Posted means the approved expense report has been entered into accounting.',
            },
            {
              src: '/assets/doc-images/expenses/image6.png',
              alt: 'Register Payment dialog for expense reimbursement',
              caption: 'Register Payment is used after posting to initiate reimbursement.',
            },
          ],
        },
        {
          id: 'done',
          title: 'Done',
          body: [
            'This is the final state of the expense process. At this stage, the expense has been processed by the accounts department, and the reimbursement has been completed or recorded in the accounting system.',
            'Purpose: to indicate that the expense process is fully completed.',
            'After clicking Register Payment and then Create Payment, the reimbursement process reaches completion.',
          ],
          figure: {
            src: '/assets/doc-images/expenses/image7.png',
            alt: 'Expense report summary in Done state with paid ribbon',
            caption: 'Done indicates that the reimbursement has been completed and the expense process is fully closed.',
          },
        },
      ],
    },
    {
      id: 'my-reports',
      title: 'My Reports',
      body: [
        'Go to Expenses -> My Expenses -> My Reports.',
        'The My Reports menu allows employees to view and manage all the expense reports created by them. An expense report is a collection of one or more expenses that are submitted together for approval.',
        'Employees can organize their individual expenses into a report and send it to their manager for verification and approval.',
      ],
      figure: {
        src: '/assets/doc-images/expenses/image2.png',
        alt: 'Employee expense report summary from My Reports',
        caption: 'My Reports groups individual expenses into a report that can be submitted for approval.',
      },
    },
    {
      id: 'expense-reports',
      title: 'Expense Reports',
      body: [
        'Go to Expenses -> Expense Reports.',
        'The Expense Reports menu allows managers and administrators to view and manage all expense reports submitted by employees. It provides a centralized view of expenses that require approval or have already been processed.',
        'An expense report is a collection of one or more expenses grouped together and submitted for review.',
        'Purpose: the purpose of the Expense Reports section is to allow managers and the finance department to review, approve, and track employee expense reports.',
      ],
      figure: {
        src: '/assets/doc-images/expenses/image8.png',
        alt: 'All expense reports list showing status and payment state',
        caption: 'Expense Reports gives managers and finance teams a centralized list of submitted and processed reports.',
      },
    },
    {
      id: 'configuration',
      title: 'Configuration',
      body: [
        'The Configuration area supports consistent categorization and workflow follow-up for employee expenses.',
      ],
      subsections: [
        {
          id: 'expense-categories',
          title: 'Expense Categories',
          body: [
            'Go to Expenses -> Configuration -> Expense Categories.',
            'Expense Categories define the different types of expenses that employees can select while creating an expense. These categories help the organization classify expenses properly and maintain accurate accounting records.',
          ],
          figure: {
            src: '/assets/doc-images/expenses/image9.png',
            alt: 'Expense category form for Lunch category',
            caption: 'Expense Categories classify reimbursement items such as meals, travel, and office costs.',
          },
        },
        {
          id: 'activity-types',
          title: 'Activity Types',
          body: [
            'Go to Expenses -> Configuration -> Activity Types.',
            'Activity Types are used to define different types of tasks or follow-up actions that can be scheduled in the system. These activities help users manage tasks such as approvals, reminders, meetings, or follow-ups related to expense reports.',
            'Administrators can configure activity types so that users can easily assign and track tasks within the workflow.',
            'Purpose: the purpose of Activity Types is to help users schedule and manage different activities related to expense reports, ensuring that tasks such as approvals or reviews are completed on time.',
          ],
          figure: {
            src: '/assets/doc-images/expenses/imagea.png',
            alt: 'Activity Type form for Expense Approval Review',
            caption: 'Activity Types support reminders and approval follow-up inside the expense workflow.',
          },
        },
      ],
    },
  ],
  related: ['invoice', 'payroll', 'hr'],
};

export default function ExpensesPage() {
  return <DocumentPage document={expensesDocument} />;
}
